import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { History, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import legacyCultivarImportService, {
  IMPORT_BATCH, isLegacyCultivarImportOpen,
} from '../../services/legacyCultivarImport';
import { errorMessage } from '../../utils/apiResponse';
import { quarantineSummary } from '../../utils/cultivarQuarantine';
import { useOrgTimezone, formatLocalDateInTz } from '../../utils/timezone';
import type {
  LegacyCultivarPreview, LegacyCultivarResult, LegacyCultivarRow,
} from '../../types/legacyCultivarImport';

/**
 * TEMPORARY (Rob, 2026-10-15): bring the cultivar register across from the
 * legacy system: names, botanical name, crop, synonyms, origin, parentage,
 * PBR and patent, and the quarantine entry and release dates. Each legacy
 * cultivar makes one of ours or fills one we have; nothing is overwritten.
 * Switched off on LEGACY_CULTIVAR_IMPORT_ENDS; delete this page, its route
 * and the registry's button then.
 */

const cardCls = 'bg-white border border-neutral-200 rounded-xl p-4 sm:p-6';
const errorCls = 'bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words';
const inputCls = 'w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';
const PAGE = 100;

type Filter = 'todo' | 'new' | 'fill' | 'choose' | 'done' | 'all';
const FILTERS: { key: Filter; label: string }[] = [
  { key: 'todo', label: 'To import' },
  { key: 'new', label: 'New' },
  { key: 'fill', label: 'Fills one of ours' },
  { key: 'choose', label: 'Needs a choice' },
  { key: 'done', label: 'Up to date' },
  { key: 'all', label: 'All' },
];

const FILL_LABELS: Record<string, string> = {
  botanical_name: 'botanical name', trade_name: 'trade name', origin_country: 'origin',
  breeding_history: 'parentage', first_sale_date_au: 'first sale', crop_type_id: 'crop type',
  quarantine_entry_date: 'quarantine entry', quarantine_release_date: 'quarantine release',
  quarantine_released: 'released from quarantine', legacy_code: 'legacy code',
};
const HOW: Record<string, string> = {
  linked: 'imported before', name: 'same name', chosen: 'chosen here',
};
const STATUS: Record<string, string> = {
  protected: 'Protected', expired: 'Protection expired', open: 'Open (no PBR or patent)',
};

function Badge({ children, tone }: { children: React.ReactNode; tone: 'green' | 'grey' | 'amber' | 'blue' }) {
  const cls = tone === 'green' ? 'bg-green-50 text-green-700 border-green-200'
    : tone === 'amber' ? 'bg-amber-50 text-amber-800 border-amber-200'
      : tone === 'blue' ? 'bg-blue-50 text-blue-700 border-blue-200'
        : 'bg-neutral-50 text-neutral-500 border-neutral-200';
  return <span className={`inline-block shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium ${cls}`}>{children}</span>;
}

/** The row's state with any hand choice applied. */
function rowState(row: LegacyCultivarRow, match: number | 'new' | undefined): Exclude<Filter, 'todo' | 'all'> {
  if (match === 'new') return 'new';
  if (match != null) return 'fill';
  if (row.blocked) return 'choose';
  if (row.nothing_to_do) return 'done';
  return row.target ? 'fill' : 'new';
}

function CultivarRow({ row, selected, onSelect, match, onMatch, choices, fmt }: {
  row: LegacyCultivarRow;
  selected: boolean;
  onSelect: (on: boolean) => void;
  match: number | 'new' | undefined;
  onMatch: (m: number | 'new' | undefined) => void;
  choices: LegacyCultivarPreview['choices'];
  fmt: (d: string) => string;
}) {
  const [choosing, setChoosing] = useState(row.blocked);
  const state = rowState(row, match);
  const selectable = state !== 'done' && state !== 'choose';
  const quarantine = quarantineSummary(row.fields, fmt);
  const chosenName = typeof match === 'number' ? choices.find((c) => c.id === match)?.name : null;
  const fills = Object.keys(row.fills).filter((f) => FILL_LABELS[f]).map((f) => FILL_LABELS[f]);
  const notImported = [
    row.not_imported.comments || row.not_imported.general_comments ? 'comments' : null,
    row.not_imported.chill_units ? `chill ${row.not_imported.chill_units}` : null,
    row.not_imported.licensor ? `licensor ${row.not_imported.licensor}` : null,
    row.not_imported.not_for_sale ? 'marked not for sale' : null,
  ].filter(Boolean);

  return (
    <li className="py-3 flex items-start gap-3">
      <input
        type="checkbox"
        aria-label={`Import ${row.name}`}
        className="mt-1 h-4 w-4 shrink-0 rounded border-neutral-300 text-primary-600 focus:ring-primary-500 disabled:opacity-40"
        checked={selected && selectable}
        disabled={!selectable}
        onChange={(e) => onSelect(e.target.checked)}
      />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <p className="min-w-0 text-sm font-medium text-neutral-800 break-words">
            {row.name}
            <span className="ml-2 text-xs font-normal text-neutral-500 break-all">{row.cult_id}{row.crop_label ? ` · ${row.crop_label}` : ''}</span>
          </p>
          {state === 'new' ? <Badge tone="blue">New cultivar</Badge>
            : state === 'fill' ? <Badge tone="green">Fills {chosenName ?? row.target?.name}</Badge>
              : state === 'choose' ? <Badge tone="amber">Choose a cultivar</Badge>
                : <Badge tone="grey">Up to date</Badge>}
        </div>
        {match == null && row.target && (
          <p className="text-xs text-neutral-500 break-words">
            <Link to={`/cultivar/${row.target.id}`} className="text-primary-600 hover:underline">{row.target.name}</Link>
            {` (${HOW[row.target.how] ?? row.target.how})`}
          </p>
        )}
        <p className="text-xs text-neutral-600 break-words">
          {STATUS[row.protection_status]}
          {row.legacy_protections.map((p) => ` · ${p.type === 'pbr' ? 'PBR' : 'US patent'} ${p.registration_number ?? ''}${p.expiry_date ? ` to ${fmt(p.expiry_date)}` : ''}`).join('')}
          {quarantine ? ` · Quarantine: ${quarantine}` : ''}
        </p>
        {state !== 'done' && match == null && (
          <p className="text-xs text-neutral-500 break-words">
            {row.target ? (fills.length ? `Fills ${fills.join(', ')}` : 'Fills no fields') : 'Takes every field'}
            {row.synonyms.length ? `; adds ${row.synonyms.length} synonym${row.synonyms.length === 1 ? '' : 's'} (${row.synonyms.map((s) => s.synonym).join(', ')})` : ''}
            {row.protections.length ? `; ${row.protections.length} protection${row.protections.length === 1 ? '' : 's'}` : ''}
          </p>
        )}
        {row.skipped_synonyms.length > 0 && (
          <p className="text-xs text-neutral-500 break-words">
            Left out: {row.skipped_synonyms.map((s) => s.synonym).join(', ')} (already another cultivar&rsquo;s name or synonym)
          </p>
        )}
        {notImported.length > 0 && (
          <p className="text-xs text-neutral-400 break-words">Not brought across: {notImported.join(', ')}</p>
        )}
        {row.problems.map((p) => (
          <p key={p} className="flex items-start gap-1 text-xs text-amber-800 break-words">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /><span className="min-w-0">{p}</span>
          </p>
        ))}
        {choosing ? (
          <select
            aria-label={`Cultivar for ${row.name}`}
            className={`${inputCls} mt-1`}
            value={match == null ? '' : String(match)}
            onChange={(e) => {
              const v = e.target.value;
              onMatch(v === '' ? undefined : v === 'new' ? 'new' : Number(v));
              if (v !== '') onSelect(true);
            }}
          >
            <option value="">{row.blocked ? 'Choose…' : 'As found'}</option>
            <option value="new">Import as a new cultivar</option>
            {choices.map((c) => <option key={c.id} value={c.id}>Fill {c.name}</option>)}
          </select>
        ) : (
          <button type="button" className="text-xs text-primary-600 hover:underline" onClick={() => setChoosing(true)}>
            Choose a different cultivar
          </button>
        )}
      </div>
    </li>
  );
}

const OUTCOME: Record<LegacyCultivarResult['outcome'], string> = {
  created: 'created', filled: 'filled', unchanged: 'already up to date', skipped: 'skipped', failed: 'failed',
};

function LegacyCultivarImport() {
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();
  const fmt = useCallback((d: string) => formatLocalDateInTz(d, orgTz), [orgTz]);
  const { hasAccess, loading: seatLoading } = useModuleLicense('cultivar', 'admin');

  const [preview, setPreview] = useState<LegacyCultivarPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [crops, setCrops] = useState<Record<string, number | null>>({});
  const [matches, setMatches] = useState<Record<string, number | 'new'>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<Filter>('todo');
  const [search, setSearch] = useState('');
  const [shown, setShown] = useState(PAGE);
  const [progress, setProgress] = useState<{ done: number; of: number } | null>(null);
  const [results, setResults] = useState<LegacyCultivarResult[] | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await legacyCultivarImportService.preview();
      if (res.success && res.data) {
        const data = res.data;
        setPreview(data);
        setCrops(Object.fromEntries(data.crops.map((c) => [c.label ?? '', c.suggested_crop_type_id])));
        setMatches({});
        setSelected(new Set(data.cultivars.filter((r) => !r.blocked && !r.nothing_to_do).map((r) => r.cult_id)));
      } else setError(errorMessage(res, 'Could not read the legacy cultivar register'));
    } catch (e) {
      setError(errorMessage(e, 'Could not read the legacy cultivar register'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasAccess && isLegacyCultivarImportOpen()) load();
  }, [hasAccess, load]);

  const rows = useMemo(() => {
    if (!preview) return [];
    const q = search.trim().toLowerCase();
    return preview.cultivars.filter((r) => {
      const state = rowState(r, matches[r.cult_id]);
      if (filter === 'todo' && (state === 'done')) return false;
      if (filter !== 'todo' && filter !== 'all' && state !== filter) return false;
      if (!q) return true;
      return [r.name, r.cult_id, r.crop_label, r.target?.name, ...r.legacy_synonyms.map((s) => s.synonym)]
        .some((v) => v && v.toLowerCase().includes(q));
    });
  }, [preview, filter, search, matches]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { new: 0, fill: 0, choose: 0, done: 0 };
    for (const r of preview?.cultivars ?? []) c[rowState(r, matches[r.cult_id])] += 1;
    return c;
  }, [preview, matches]);

  const importable = (r: LegacyCultivarRow) => {
    const s = rowState(r, matches[r.cult_id]);
    return s === 'new' || s === 'fill';
  };
  const chosen = (preview?.cultivars ?? []).filter((r) => selected.has(r.cult_id) && importable(r));
  const shownSelectable = rows.filter(importable);
  const allShownSelected = shownSelectable.length > 0 && shownSelectable.every((r) => selected.has(r.cult_id));

  const toggle = (id: string, on: boolean) => setSelected((s) => {
    const next = new Set(s);
    if (on) next.add(id); else next.delete(id);
    return next;
  });

  const runImport = async () => {
    if (!chosen.length) return;
    setError(null); setResults(null);
    const ids = chosen.map((r) => r.cult_id);
    const all: LegacyCultivarResult[] = [];
    setProgress({ done: 0, of: ids.length });
    try {
      for (let i = 0; i < ids.length; i += IMPORT_BATCH) {
        const batch = ids.slice(i, i + IMPORT_BATCH);
        const batchMatches = Object.fromEntries(batch.filter((id) => matches[id] != null).map((id) => [id, matches[id]]));
        const res = await legacyCultivarImportService.import(batch, crops, batchMatches);
        if (!res.success || !res.data) {
          setError(errorMessage(res, 'The import stopped part way'));
          break;
        }
        all.push(...res.data.results);
        setProgress({ done: Math.min(i + batch.length, ids.length), of: ids.length });
      }
    } catch (e) {
      setError(errorMessage(e, 'The import stopped part way'));
    } finally {
      setResults(all);
      setProgress(null);
      // What is now true, from the server.
      load();
    }
  };

  const header = (
    <PageHeader
      title="Import cultivars"
      subtitle="The cultivar register from hh.factree: names, crops, synonyms, PBR, patents and quarantine dates"
      icon={<History className="h-6 w-6 text-white" />}
      actionButtons={(
        <Button variant="outline" size="sm" onClick={() => navigate('/cultivar')}>Back</Button>
      )}
    />
  );

  if (seatLoading) return <>{header}<div className="max-w-5xl mx-auto px-4 py-6"><div className="h-40 bg-neutral-100 rounded animate-pulse" /></div></>;
  if (!hasAccess) {
    return (
      <>{header}
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className={cardCls}><p className="text-sm text-neutral-700">Importing cultivars needs a Cultivar Admin seat.</p></div>
        </div>
      </>
    );
  }
  if (!isLegacyCultivarImportOpen()) {
    return (
      <>{header}
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className={cardCls}><p className="text-sm text-neutral-700">Importing cultivars from the legacy system has been switched off.</p></div>
        </div>
      </>
    );
  }

  const tally = (o: LegacyCultivarResult['outcome']) => (results ?? []).filter((r) => r.outcome === o).length;
  const problems = (results ?? []).filter((r) => r.outcome === 'skipped' || r.outcome === 'failed');

  return (
    <>
      {header}
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        <div className={`${cardCls} space-y-2 text-sm text-neutral-700`}>
          <p>
            Each legacy cultivar becomes one of yours, or fills one you already have: the one imported before, or the one
            with the same name or synonym. Nothing you have is overwritten; blank fields are filled and missing synonyms
            and protections added. Nothing is saved until you press Import.
          </p>
          <p className="text-xs text-neutral-500">
            New cultivars are private. One with a current PBR or patent waits in FactreeTech&rsquo;s rights review; one with
            none is open, which shows it to every organisation. Comments, chill units, maturity, vigour, licensor and the
            not-for-sale flag are not brought across. This page stops working on 28 October 2026.
          </p>
        </div>

        {error && <div className={errorCls}>{error}</div>}

        {results && (
          <div className={`${cardCls} space-y-2`}>
            <p className="flex items-start gap-2 text-sm text-neutral-800">
              <CheckCircle2 className="h-5 w-5 shrink-0 text-green-600" />
              <span className="min-w-0 break-words">
                {`${tally('created')} created, ${tally('filled')} filled, ${tally('unchanged')} already up to date`}
                {problems.length ? `, ${problems.length} not imported` : ''}.
              </span>
            </p>
            {problems.length > 0 && (
              <ul className="text-xs text-neutral-600 space-y-1">
                {problems.map((r) => (
                  <li key={r.cult_id} className="break-words">
                    <span className="font-medium">{r.name}</span> {OUTCOME[r.outcome]}: {r.reason}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {loading && !preview && (
          <div className={`${cardCls} flex items-center gap-2 text-sm text-neutral-600`}>
            <Loader2 className="h-4 w-4 animate-spin" /> Reading the legacy cultivar register…
          </div>
        )}

        {preview && (
          <>
            {!preview.protection_columns && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800 break-words">
                The legacy database has no patent number, patent expiry or PBR expiry columns, so only PBR numbers come across.
              </div>
            )}

            <div className={`${cardCls} space-y-3`}>
              <h2 className="text-sm font-semibold text-neutral-900">Crop types</h2>
              <p className="text-xs text-neutral-500">
                The crop each legacy crop name becomes. Only fills a cultivar with no crop type.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {preview.crops.map((c) => (
                  <label key={c.label ?? ''} className="block min-w-0 text-sm text-neutral-700">
                    <span className="block break-words">{c.label ?? 'No crop'} <span className="text-xs text-neutral-500">({c.count})</span></span>
                    <select
                      className={`${inputCls} mt-1`}
                      value={crops[c.label ?? ''] ?? ''}
                      onChange={(e) => setCrops((m) => ({ ...m, [c.label ?? '']: e.target.value ? Number(e.target.value) : null }))}
                    >
                      <option value="">Leave blank</option>
                      {preview.crop_types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </label>
                ))}
              </div>
            </div>

            <div className={`${cardCls} space-y-3`}>
              <div className="flex flex-wrap gap-2">
                {FILTERS.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => { setFilter(f.key); setShown(PAGE); }}
                    className={`rounded-full border px-3 py-1 text-xs font-medium ${filter === f.key
                      ? 'border-primary-600 bg-primary-50 text-primary-700'
                      : 'border-neutral-200 text-neutral-600 hover:bg-neutral-50'}`}
                  >
                    {f.label}
                    {f.key === 'todo' ? ` (${counts.new + counts.fill + counts.choose})`
                      : f.key === 'all' ? ` (${preview.cultivars.length})`
                        : ` (${counts[f.key]})`}
                  </button>
                ))}
              </div>
              <input
                className={inputCls}
                placeholder="Search by name, code, crop or synonym"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setShown(PAGE); }}
              />
              <div className="flex flex-wrap items-center justify-between gap-2">
                <label className="flex items-center gap-2 text-sm text-neutral-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                    checked={allShownSelected}
                    disabled={!shownSelectable.length}
                    onChange={(e) => setSelected((s) => {
                      const next = new Set(s);
                      for (const r of shownSelectable) { if (e.target.checked) next.add(r.cult_id); else next.delete(r.cult_id); }
                      return next;
                    })}
                  />
                  Select all {shownSelectable.length} shown
                </label>
                <Button onClick={runImport} disabled={!chosen.length || !!progress || loading}>
                  {progress ? `Importing ${progress.done} of ${progress.of}…` : `Import ${chosen.length} cultivar${chosen.length === 1 ? '' : 's'}`}
                </Button>
              </div>

              {rows.length === 0 ? (
                <p className="text-sm text-neutral-500">Nothing here.</p>
              ) : (
                <ul className="divide-y divide-neutral-100">
                  {rows.slice(0, shown).map((r) => (
                    <CultivarRow
                      key={r.cult_id}
                      row={r}
                      selected={selected.has(r.cult_id)}
                      onSelect={(on) => toggle(r.cult_id, on)}
                      match={matches[r.cult_id]}
                      onMatch={(m) => setMatches((all) => {
                        const next = { ...all };
                        if (m == null) delete next[r.cult_id]; else next[r.cult_id] = m;
                        return next;
                      })}
                      choices={preview.choices}
                      fmt={fmt}
                    />
                  ))}
                </ul>
              )}
              {rows.length > shown && (
                <Button variant="outline" size="sm" onClick={() => setShown((n) => n + PAGE)}>
                  Show {Math.min(PAGE, rows.length - shown)} more of {rows.length - shown}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default LegacyCultivarImport;
